import {
  ApiException,
  DefaultApi,
  EventFormat,
  Filters,
  GetActiveContractsRequest,
  GetUpdatesRequest,
  JsCantonError,
  JsGetActiveContractsResponse,
  JsGetUpdatesResponse,
} from '@digitalasset/json-api-ts-client';
import { WSSupport } from '../WSSupport';
import { ACSCacheUtils } from './ACSCacheUtils';
import { ACSContainerLogger } from './ACSContainerLogger';
import { ACSKeyData, ACSUpdateConfig, InitializedACSSet } from './ACSTypes';

export class ACSContainerHttpUtils {
  public static async readUpdates(
    startOffset: number,
    endOffset: number,
    eventFormat: EventFormat,
    api: DefaultApi,
    config: ACSUpdateConfig,
    LOG: ACSContainerLogger | null,
  ): Promise<Array<JsGetUpdatesResponse>> {
    const updates: GetUpdatesRequest = {
      beginExclusive: startOffset,
      endInclusive: endOffset,
      updateFormat: {
        includeTransactions: {
          eventFormat,
          transactionShape: 'TRANSACTION_SHAPE_ACS_DELTA',
        },
      },
      verbose: false,
    };
    return api.postV2UpdatesFlats(updates, config.maxUpdatesToFetch, 1000).catch((error: Error) => {
      LOG?.gotErrorDuringFetchingACSUpdatesUsingHTTP(error);
      throw error;
    });
  }

  public static async readACS(
    offset: number,
    key: ACSKeyData,
    api: DefaultApi,
    wsSupport: WSSupport,
    config: ACSUpdateConfig,
    LOG: ACSContainerLogger | null = null,
  ): Promise<InitializedACSSet> {
    if (wsSupport.enabled()) {
      return ACSContainerHttpUtils.readACSUsingWs(offset, key, wsSupport, config, LOG).catch(() => {
        console.log('Falling back to HTTP for ACS read');
        return ACSContainerHttpUtils.readHttpACS(offset, key, api, wsSupport, LOG);
      });
    }
    return ACSContainerHttpUtils.readHttpACS(offset, key, api, wsSupport, LOG);
  }

  public static createEventFormat(key: ACSKeyData): EventFormat {
    const cumulativeFilter = key.templateId
      ? this.createTemplateFilter(key.templateId)
      : this.createInterfaceFilter(key.interfaceId ?? '');

    if (key.party) {
      return {
        filtersByParty: {
          [key.party]: cumulativeFilter,
        },
        verbose: false,
      };
    }
    return {
      filtersByParty: {},
      filtersForAnyParty: cumulativeFilter,
      verbose: false,
    };
  }

  private static createTemplateFilter(templateId: string): Filters {
    return {
      cumulative: [
        {
          identifierFilter: {
            TemplateFilter: {
              value: {
                templateId,
                includeCreatedEventBlob: false,
              },
            },
          },
        },
      ],
    };
  }

  private static createInterfaceFilter(interfaceId: string): Filters {
    return {
      cumulative: [
        {
          identifierFilter: {
            InterfaceFilter: {
              value: {
                interfaceId,
                includeInterfaceView: true,
                includeCreatedEventBlob: false,
              },
            },
          },
        },
      ],
    };
  }

  private static readACSUsingWs(
    offset: number,
    key: ACSKeyData,
    wsSupport: WSSupport,
    config: ACSUpdateConfig,
    LOG: ACSContainerLogger | null,
  ): Promise<InitializedACSSet> {
    const wsACSURL = `${wsSupport.baseUrl}/v2/state/active-contracts`;
    const format = this.createEventFormat(key);
    const request: GetActiveContractsRequest = {
      activeAtOffset: offset,
      verbose: false,
      eventFormat: format,
    };
    return new Promise<InitializedACSSet>((resolve, reject) => {
      const ws = new WebSocket(wsACSURL, wsSupport.extractProtocols());
      const results: JsGetActiveContractsResponse[] = [];
      let finished = false;
      let error: Error | null = null;
      setTimeout(() => {
        if (!finished && !error && results.length === 0) {
          error = Error(
            `No data received from WebSocket ${wsACSURL} within ${config.wsTimeoutBeforeFirstElement}ms`,
          );
          wsSupport.reportError(error);
          reject(error);
          ws.close();
        }
      }, config.wsTimeoutBeforeFirstElement);
      ws.onopen = () => {
        ws.send(JSON.stringify(request));
      };

      ws.onmessage = (event: MessageEvent<string>) => {
        try {
          const data = JSON.parse(event.data) as JsGetActiveContractsResponse;
          results.push(data);
        } catch (err) {
          console.error('Invalid JSON:', err);
        }
      };

      ws.onerror = () => {
        error = new Error('WebSocket error');
        wsSupport.reportError(error);
        reject(error);
      };

      ws.onclose = () => {
        finished = true;
        if (!error) {
          wsSupport.reportSuccess();
          resolve({
            key: ACSCacheUtils.keyDataToString(key),
            initialized: true,
            creationTimestampMs: Date.now(),
            acsOffset: offset,
            acs: results,
          });
        }
      };
    }).catch((error: Error) => {
      LOG?.gotErrorDuringFetchingEntireACSUsingWebSockets(error);
      throw error;
    });
  }

  private static async readHttpACS(
    offset: number,
    key: ACSKeyData,
    api: DefaultApi,
    wsSupport: WSSupport,
    LOG: ACSContainerLogger | null,
  ): Promise<InitializedACSSet> {
    const format = ACSContainerHttpUtils.createEventFormat(key);
    // 0 means no limit - we handle the case where the result is too large below
    const effectiveQueryLimit = wsSupport.queryLimit === 0 ? undefined : wsSupport.queryLimit;
    const acs = await api
      .postV2StateActiveContracts(
        {
          activeAtOffset: offset,
          verbose: false,
          eventFormat: format,
        },
        effectiveQueryLimit,
      )
      .catch((error: ApiException<JsCantonError>) => {
        LOG?.gotErrorDuringFetchingEntireACSUsingHTTP(error);

        // this catch clause is specifically to handle the case where the ACS is too large to be returned in one go, but client has not set a query limit (or it is 0)
        // this allows to extract the server limit from the error message and retry with that limit
        // whole section can be removed once server side pagination is implemented (or websockets work without problems)
        if (error.code !== 413 || effectiveQueryLimit !== undefined) {
          throw error;
        }
        const errorMessage = error?.body?.cause ?? '';
        const matchNmb = errorMessage.match(/limit\s*\((\d+)\)/);

        if (!matchNmb || matchNmb.length <= 1) {
          console.log(`Could not extract limit from error message: ${errorMessage}`);
          throw error;
        }

        const serverLimit = parseInt(matchNmb[1], 10);
        if (Number.isNaN(serverLimit)) {
          console.log(`Could not extract server limit from error message: ${errorMessage}`);
          throw error;
        }
        console.error(
          `The result size limit of server ${serverLimit} was exceeded. In order to keep application running limit is added to active-contracts query - this means that potentially not all the data is displayed.`,
        );
        // in order to avoid repeated extraction of the limit from the error message, we set it in wsSupport - this means that subsequent queries will also use that limit
        wsSupport.setQueryLimit(serverLimit);
        return api.postV2StateActiveContracts(
          {
            activeAtOffset: offset,
            verbose: false,
            eventFormat: format,
          },
          serverLimit,
        );
      });
    return {
      key: ACSCacheUtils.keyDataToString(key),
      initialized: true,
      creationTimestampMs: Date.now(),
      acsOffset: offset,
      acs,
    };
  }
}
